import { createContext, useContext, useState } from "react";

interface SidebarContextType {
	isCollapsed: boolean;
	toggleSidebar: () => void;
	setIsCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const SIDEBAR_STORAGE_KEY = "sidebarCollapsed";

// Função auxiliar para ler do localStorage
function readFromStorage(): boolean {
	try {
		const savedState = localStorage.getItem(SIDEBAR_STORAGE_KEY);
		if (savedState === null) {
			return false; // Padrão: expandido
		}
		return savedState === "true";
	} catch (error) {
		console.error("[SidebarContext] Error reading from localStorage:", error);
		return false;
	}
}

// Função auxiliar para salvar no localStorage
function saveToStorage(value: boolean): void {
	try {
		localStorage.setItem(SIDEBAR_STORAGE_KEY, String(value));
	} catch (error) {
		console.error("[SidebarContext] Error saving to localStorage:", error);
	}
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
	// Inicializar estado lendo do localStorage
	const [isCollapsed, setIsCollapsedState] = useState<boolean>(() => readFromStorage());

	// Wrapper que salva no localStorage E atualiza o estado
	const setIsCollapsed = (collapsed: boolean) => {
		setIsCollapsedState(collapsed);
		saveToStorage(collapsed);
	};

	// Toggle que salva no localStorage E atualiza o estado
	const toggleSidebar = () => {
		setIsCollapsedState((prev) => {
			const newValue = !prev;
			saveToStorage(newValue);
			return newValue;
		});
	};

	const value = {
		isCollapsed,
		toggleSidebar,
		setIsCollapsed,
	};

	return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
	const context = useContext(SidebarContext);
	if (context === undefined) {
		throw new Error("useSidebar must be used within a SidebarProvider");
	}
	return context;
}
